/**
 * Application Theme Configuration
 * 
 * Integrates semantic color tokens with Material-UI's theme system
 */

import { createTheme, ThemeOptions } from '@mui/material/styles';
import type { PaletteMode } from '@mui/material';
import { createColorTokens, ThemeIntensity } from './tokens';
import { getColorScheme, lightenColor, darkenColor } from './colorSchemes';
import { metalSx } from './metalButtons';

/**
 * Custom colors interface for user-defined color schemes
 */
interface CustomColors {
  primary?: string;
  secondary?: string;
}

/**
 * Portal target for MUI Modal/Popover/Popper: the fullscreen element when one
 * is active (so overlays stay visible in fullscreen), else document.body.
 * Evaluated per-open because the fullscreen element changes at runtime.
 */
export const fullscreenAwarePortalContainer = (): HTMLElement | null => {
  if (typeof document === 'undefined') return null;
  return (document.fullscreenElement as HTMLElement | null) ?? document.body;
};

function normalizeHexColor(value: string): string | null {
  const t = value.trim();
  if (/^#[0-9A-Fa-f]{6}$/.test(t)) return t.toUpperCase();
  if (/^#[0-9A-Fa-f]{3}$/.test(t)) {
    return `#${t[1]}${t[1]}${t[2]}${t[2]}${t[3]}${t[3]}`.toUpperCase();
  }
  return null;
}

function resolveThemeColor(candidate: string | undefined, fallback: string): string {
  return normalizeHexColor(candidate || '') ?? fallback;
}

/**
 * Creates the application theme with semantic color tokens
 * 
 * @param mode - MUI palette mode ('light' or 'dark')
 * @param colorSchemeId - Color scheme identifier
 * @param customColors - Custom primary/secondary colors
 * @param intensity - Theme intensity level for intermediate modes
 */
export const createAppTheme = (
  mode: PaletteMode, 
  colorSchemeId: string = 'default',
  customColors?: CustomColors,
  intensity?: ThemeIntensity
) => {
  const scheme = getColorScheme(colorSchemeId);
  const tokens = createColorTokens(mode, intensity);
  const isLight = mode === 'light';

  // Use custom colors if "custom" scheme is selected and custom colors are provided
  const primaryMain = colorSchemeId === 'custom'
    ? resolveThemeColor(customColors?.primary, scheme.colors.primary)
    : scheme.colors.primary;
  const primaryLight = isLight ? lightenColor(primaryMain, 20) : lightenColor(primaryMain, 10);
  const primaryDark = isLight ? darkenColor(primaryMain, 20) : darkenColor(primaryMain, 10);

  const secondaryMain = colorSchemeId === 'custom'
    ? resolveThemeColor(customColors?.secondary, scheme.colors.secondary)
    : scheme.colors.secondary;
  const secondaryLight = isLight ? lightenColor(secondaryMain, 20) : lightenColor(secondaryMain, 10);
  const secondaryDark = isLight ? darkenColor(secondaryMain, 20) : darkenColor(secondaryMain, 10);

  const themeOptions: ThemeOptions = {
    palette: {
      mode,
      
      // Primary brand color (from selected scheme)
      primary: {
        main: primaryMain,
        light: primaryLight,
        dark: primaryDark,
        contrastText: '#ffffff',
      },
      
      // Secondary brand color (from selected scheme)
      secondary: {
        main: secondaryMain,
        light: secondaryLight,
        dark: secondaryDark,
        contrastText: '#ffffff',
      },
      
      // Semantic colors
      success: {
        main: tokens.semantic.success.main,
        light: tokens.semantic.success.background,
        dark: tokens.semantic.success.border,
        contrastText: '#ffffff',
      },
      warning: {
        main: tokens.semantic.warning.main,
        light: tokens.semantic.warning.background,
        dark: tokens.semantic.warning.border,
        contrastText: '#ffffff',
      },
      error: {
        main: tokens.semantic.error.main,
        light: tokens.semantic.error.background,
        dark: tokens.semantic.error.border,
        contrastText: '#ffffff',
      },
      info: {
        main: tokens.semantic.info.main,
        light: tokens.semantic.info.background,
        dark: tokens.semantic.info.border,
        contrastText: '#ffffff',
      },
      
      // Background colors
      background: {
        default: tokens.background.default,
        paper: tokens.background.paper,
      },
      
      // Text colors
      text: {
        primary: tokens.text.primary,
        secondary: tokens.text.secondary,
        disabled: tokens.text.disabled,
      },
      
      // Divider color
      divider: tokens.border.default,
      
      // Action states
      action: {
        active: tokens.text.primary,
        hover: tokens.component.sidebar.hover,
        selected: tokens.component.sidebar.active,
        disabled: tokens.text.disabled,
        disabledBackground: tokens.component.input.disabled,
        focus: isLight ? `${primaryMain}1f` : `${primaryMain}3d`,
      },
    },
    
    // Typography
    typography: {
      fontFamily: "'Control Upright TT', Arial, sans-serif",
      h1: {
        fontWeight: 700,
        fontSize: '2.5rem',
        lineHeight: 1.2,
        color: tokens.text.primary,
      },
      h2: {
        fontWeight: 700,
        fontSize: '2rem',
        lineHeight: 1.3,
        color: tokens.text.primary,
      },
      h3: {
        fontWeight: 600,
        fontSize: '1.75rem',
        lineHeight: 1.4,
        color: tokens.text.primary,
      },
      h4: {
        fontWeight: 600,
        fontSize: '1.5rem',
        lineHeight: 1.4,
        color: tokens.text.primary,
      },
      h5: {
        fontWeight: 600,
        fontSize: '1.25rem',
        lineHeight: 1.5,
        color: tokens.text.primary,
      },
      h6: {
        fontWeight: 600,
        fontSize: '1rem',
        lineHeight: 1.6,
        color: tokens.text.primary,
      },
      body1: {
        fontSize: '1rem',
        lineHeight: 1.5,
        color: tokens.text.primary,
      },
      body2: {
        fontSize: '0.875rem',
        lineHeight: 1.57,
        color: tokens.text.secondary,
      },
      caption: {
        fontSize: '0.75rem',
        lineHeight: 1.66,
        color: tokens.text.tertiary,
      },
      overline: {
        fontSize: '0.75rem',
        fontWeight: 600,
        lineHeight: 2.66,
        textTransform: 'uppercase',
        letterSpacing: '0.08em',
        color: tokens.text.tertiary,
      },
    },
    
    // Shape
    shape: {
      borderRadius: 8,
    },
    
    // Shadows
    shadows: [
      'none',
      tokens.shadow.sm,
      tokens.shadow.sm,
      tokens.shadow.md,
      tokens.shadow.md,
      tokens.shadow.md,
      tokens.shadow.lg,
      tokens.shadow.lg,
      tokens.shadow.lg,
      tokens.shadow.xl,
      tokens.shadow.xl,
      tokens.shadow.xl,
      tokens.shadow.xl,
      tokens.shadow.xl,
      tokens.shadow.xl,
      tokens.shadow.xl,
      tokens.shadow.xl,
      tokens.shadow.xl,
      tokens.shadow.xl,
      tokens.shadow.xl,
      tokens.shadow.xl,
      tokens.shadow.xl,
      tokens.shadow.xl,
      tokens.shadow.xl,
      tokens.shadow.xl,
    ],
    
    // Component overrides
    components: {
      // Portaled surfaces (dialogs, menus, selects, tooltips) must mount inside
      // the element that is currently fullscreen, otherwise they render into
      // document.body behind the fullscreen layer and are invisible — e.g. the
      // Pathfinder Map Settings dialog while the map column is fullscreen.
      MuiModal: { defaultProps: { container: fullscreenAwarePortalContainer } },
      MuiPopover: { defaultProps: { container: fullscreenAwarePortalContainer } },
      MuiPopper: { defaultProps: { container: fullscreenAwarePortalContainer } },
      // Button — machined metal faces. Contained primary is steel; outlined
      // is gunmetal; error / warning are anodized red and hazard amber.
      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            fontWeight: 600,
            borderRadius: '9px',
            padding: '8px 16px',
          },
          contained: metalSx('steel'),
          containedPrimary: metalSx('steel'),
          containedSecondary: metalSx('titanium'),
          containedError: metalSx('anodized'),
          containedWarning: metalSx('hazard'),
          containedSuccess: metalSx('titanium'),
          containedInfo: metalSx('steel'),
          outlined: metalSx('gun'),
          outlinedPrimary: metalSx('gun'),
          outlinedSecondary: metalSx('gun'),
          outlinedError: metalSx('anodized'),
          outlinedWarning: metalSx('hazard'),
          outlinedSuccess: metalSx('gun'),
          outlinedInfo: metalSx('gun'),
        },
      },
      
      // Card
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 12,
            border: `1px solid ${tokens.border.default}`,
            boxShadow: tokens.shadow.sm,
            '&:hover': {
              boxShadow: tokens.shadow.md,
            },
          },
        },
      },

      // Alert — refined "info bar" styling: subtle tinted surface with a left
      // accent rail and a normal-weight text color, so notices and inline tips
      // read as intentional callouts rather than default Bootstrap-y alert boxes.
      MuiAlert: {
        styleOverrides: {
          root: {
            borderRadius: 10,
            paddingTop: 8,
            paddingBottom: 8,
            paddingLeft: 14,
            alignItems: 'center',
            border: `1px solid ${tokens.border.subtle}`,
            boxShadow: 'none',
            backgroundImage: 'none',
          },
          message: {
            paddingTop: 2,
            paddingBottom: 2,
            fontSize: '0.875rem',
            lineHeight: 1.5,
          },
          icon: {
            opacity: 1,
            alignItems: 'center',
          },
          action: {
            alignItems: 'center',
            paddingTop: 0,
          },
          standardInfo: {
            backgroundColor: tokens.semantic.info.background,
            borderLeft: `3px solid ${tokens.semantic.info.main}`,
            color: tokens.text.primary,
            '& .MuiAlert-icon': { color: tokens.semantic.info.main },
          },
          standardSuccess: {
            backgroundColor: tokens.semantic.success.background,
            borderLeft: `3px solid ${tokens.semantic.success.main}`,
            color: tokens.text.primary,
            '& .MuiAlert-icon': { color: tokens.semantic.success.main },
          },
          standardWarning: {
            backgroundColor: tokens.semantic.warning.background,
            borderLeft: `3px solid ${tokens.semantic.warning.main}`,
            color: tokens.text.primary,
            '& .MuiAlert-icon': { color: tokens.semantic.warning.main },
          },
          standardError: {
            backgroundColor: tokens.semantic.error.background,
            borderLeft: `3px solid ${tokens.semantic.error.main}`,
            color: tokens.text.primary,
            '& .MuiAlert-icon': { color: tokens.semantic.error.main },
          },
          outlinedInfo: { borderLeft: `3px solid ${tokens.semantic.info.main}` },
          outlinedSuccess: { borderLeft: `3px solid ${tokens.semantic.success.main}` },
          outlinedWarning: { borderLeft: `3px solid ${tokens.semantic.warning.main}` },
          outlinedError: { borderLeft: `3px solid ${tokens.semantic.error.main}` },
        },
      },

      // Alert title — tighter, slightly bolder than body for clear hierarchy
      MuiAlertTitle: {
        styleOverrides: {
          root: {
            fontWeight: 700,
            marginBottom: 2,
            color: tokens.text.primary,
          },
        },
      },
      
      // Paper
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
          },
          elevation1: {
            boxShadow: tokens.shadow.sm,
          },
          elevation2: {
            boxShadow: tokens.shadow.md,
          },
          elevation3: {
            boxShadow: tokens.shadow.lg,
          },
        },
      },
      
      // Input
      MuiTextField: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              '& fieldset': {
                borderColor: tokens.component.input.border,
              },
              '&:hover fieldset': {
                borderColor: tokens.border.strong,
              },
              '&.Mui-focused fieldset': {
                borderColor: tokens.border.focus,
              },
            },
            // Date and datetime-local input styling - icons match text color, full-box clickable
            '& input[type="date"], & input[type="datetime-local"], & input[type="time"]': {
              cursor: 'pointer',
              colorScheme: isLight ? 'light' : 'dark',
              '&::-webkit-calendar-picker-indicator': {
                cursor: 'pointer',
                position: 'absolute',
                top: 0,
                right: 0,
                bottom: 0,
                left: 0,
                width: 'auto',
                height: 'auto',
                background: 'transparent',
                // Use theme-aware icon color
                backgroundImage: isLight 
                  ? `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='20' height='20' viewBox='0 0 24 24' fill='none' stroke='%23333333' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Crect x='3' y='4' width='18' height='18' rx='2' ry='2'%3E%3C/rect%3E%3Cline x1='16' y1='2' x2='16' y2='6'%3E%3C/line%3E%3Cline x1='8' y1='2' x2='8' y2='6'%3E%3C/line%3E%3Cline x1='3' y1='10' x2='21' y2='10'%3E%3C/line%3E%3C/svg%3E")`
                  : `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='20' height='20' viewBox='0 0 24 24' fill='none' stroke='%23f5f5f5' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Crect x='3' y='4' width='18' height='18' rx='2' ry='2'%3E%3C/rect%3E%3Cline x1='16' y1='2' x2='16' y2='6'%3E%3C/line%3E%3Cline x1='8' y1='2' x2='8' y2='6'%3E%3C/line%3E%3Cline x1='3' y1='10' x2='21' y2='10'%3E%3C/line%3E%3C/svg%3E")`,
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'right 8px center',
                backgroundSize: '18px',
                color: 'transparent',
              },
            },
          },
        },
      },
      
      // Select
      MuiSelect: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: tokens.component.input.border,
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: tokens.border.strong,
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: tokens.border.focus,
            },
          },
        },
      },
      
      // Table
      MuiTableHead: {
        styleOverrides: {
          root: {
            backgroundColor: tokens.component.table.headerBackground,
          },
        },
      },
      MuiTableRow: {
        styleOverrides: {
          root: {
            '&:nth-of-type(odd)': {
              backgroundColor: tokens.component.table.rowBackgroundAlt,
            },
            '&:hover': {
              backgroundColor: tokens.component.table.rowHover,
            },
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            borderColor: tokens.border.subtle,
          },
        },
      },
      
      // Tooltip
      MuiTooltip: {
        defaultProps: {
          arrow: true,
        },
        styleOverrides: {
          tooltip: {
            maxWidth: 300,
            backgroundColor: tokens.component.tooltip.background,
            color: tokens.component.tooltip.text,
            fontSize: '0.72rem',
            fontWeight: 500,
            lineHeight: 1.5,
            padding: '8px 10px',
            borderRadius: 10,
            border: `1px solid ${tokens.component.tooltip.border}`,
            boxShadow: '0 8px 24px rgba(0,0,0,0.45)',
          },
          arrow: {
            color: tokens.component.tooltip.background,
            '&::before': {
              border: `1px solid ${tokens.component.tooltip.border}`,
              backgroundColor: tokens.component.tooltip.background,
              boxSizing: 'border-box',
            },
          },
        },
      },
      
      // Dialog
      MuiDialog: {
        styleOverrides: {
          paper: {
            backgroundImage: 'none',
            boxShadow: tokens.shadow.xl,
          },
        },
      },
      
      // Chip
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: 6,
          },
        },
      },
      
      // Tabs — sharp scheme accent on the active indicator (small, high-emphasis)
      MuiTabs: {
        styleOverrides: {
          indicator: {
            height: 3,
            backgroundColor: secondaryMain,
            borderRadius: '3px 3px 0 0',
          },
        },
      },

      // Progress — scheme-driven fill with rounded ends
      MuiLinearProgress: {
        styleOverrides: {
          root: {
            borderRadius: 999,
            backgroundColor: `${primaryMain}1f`,
          },
          bar: {
            borderRadius: 999,
          },
        },
      },
      
      // Drawer
      MuiDrawer: {
        styleOverrides: {
          paper: {
            borderRight: `1px solid ${tokens.border.default}`,
          },
        },
      },
      
      // Global scrollbar styles — light grey everywhere
      MuiCssBaseline: {
        styleOverrides: {
          '*::-webkit-scrollbar': {
            width: '6px',
            height: '6px',
          },
          '*::-webkit-scrollbar-track': {
            background: 'transparent',
          },
          '*::-webkit-scrollbar-thumb': {
            background: isLight ? 'rgba(0, 0, 0, 0.25)' : 'rgba(255, 255, 255, 0.3)',
            borderRadius: '3px',
          },
          '*::-webkit-scrollbar-thumb:hover': {
            background: isLight ? 'rgba(0, 0, 0, 0.4)' : 'rgba(255, 255, 255, 0.5)',
          },
          // Firefox scrollbar
          '*': {
            scrollbarWidth: 'thin',
            scrollbarColor: isLight
              ? 'rgba(0, 0, 0, 0.25) transparent'
              : 'rgba(255, 255, 255, 0.3) transparent',
          },
        },
      },
    },
    
    // Add our custom tokens to the theme
    tokens,
    
    // Track the intensity level for components that need it
    themeIntensity: intensity || mode,
  };

  return createTheme(themeOptions);
};

